import type { ParseKeys } from 'i18next'
import { useTranslation } from 'react-i18next'

import { DatePicker, Field, Select } from '@/components/ui/Field'
import { toOptions } from '@/components/ui/control'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { Input } from '@/components/ui/Input'
import { useSession } from '@/features/auth/api/useSession'
import { useCases } from '@/features/cases/api/useCase'
import { counterpartyRights } from '@/features/counterparties/counterpartyList'
import { useIssuingEntityOptions } from '@/features/finance/api/useFinance'
import { DocumentLines } from '@/features/finance/components/DocumentLines'
import { PaymentPartyPicker } from '@/features/finance/components/PaymentPartyPicker'
import {
  isLineComplete,
  normaliseDecimal,
  type LineDraft,
} from '@/features/finance/lines'
import {
  INVOICE_CURRENCIES,
  currencyLabel,
  // The document dates book an FX rate (FR-55), so they are seeded from the
  // invoice module's LOCAL-date helper rather than from the UTC `today()` at the
  // bottom of this file: `toISOString` answers yesterday for everyone in this
  // company between midnight and 05:00 in Ashgabat, and a rate a day early is a
  // different figure arrived at by nobody's decision.
  today as bookingToday,
  type InvoiceCurrency,
} from '@/features/finance/invoice'
import {
  NO_PARTY,
  caseClientOf,
  partyComplete,
  seedParty,
  type PaymentParty,
} from '@/features/finance/paymentParty'
import { fieldErrorKey, fieldErrors } from '@/lib/problems'
import { refusalMessage } from '@/lib/refusals'
import { isSuppressed } from '@/types/domain'
import type { RaiseDocumentPayload } from '@/features/finance/api/useFinance'
import type {
  DebitNote,
  DebitNoteHeaderPayload,
  DebitNoteLinePayload,
  Payment,
  PaymentMethod,
} from '@/types/domain'

/* The money editors.
 *
 * Money always arrives and leaves as a decimal STRING (AD-9) — these drafts
 * never widen an amount to `number`, and the only arithmetic here is the
 * validation check, which parses a copy and throws it away. A float that has
 * been through `Number()` and back has already lost the cent nobody will find.
 *
 * Amount fields take inputMode="decimal" so a phone offers the numeric keypad:
 * Finance enters these on the move as often as at a desk.
 */

/** Accepts `1234.56`, `1 234,56` and `1,234.56`; rejects text and blanks. */
function isValidAmount(raw: string): boolean {
  const normalised = raw.replace(/\s/g, '').replace(',', '.')
  return normalised !== '' && /^\d+(\.\d{1,2})?$/.test(normalised)
}

const CURRENCIES = ['USD', 'EUR', 'CNY', 'RUB', 'TMT'] as const

/** Keyed to match `finance.payments.in` / `.out` in the catalogues. */
const DIRECTIONS: Payment['direction'][] = ['in', 'out']

/** What a reader without `counterparty.read` may record (S-22d2). */
const INBOUND_ONLY: Payment['direction'][] = ['in']

/** The server's own spellings, keyed to match `finance.payments.methods.*`. */
const METHODS: PaymentMethod[] = ['cash', 'bank_transfer']

/* ── Payment ──────────────────────────────────────────────────────────────── */

export interface PaymentDraft {
  /** The deal this money moves against. A payment is always on a case, so the
   *  Money page — which records across all of them — has to ask which. */
  caseId: string
  reference: string
  /** Whom we paid or received from, by record wherever it is one (S-22d2):
   *  re-seeded from the case's client whenever the case or the direction
   *  changes, because the party rule is the direction's. */
  party: PaymentParty
  direction: Payment['direction']
  method: PaymentMethod
  /** OUR OWN legal entity: which of the company's issuing entities received or
   *  paid the money. Not the counterparty, who is named beside it. */
  issuingEntityId: string
  amount: string
  currency: string
  /** What the bank charged to move the money (migration 0058). OPTIONAL, and
   *  BLANK means no fee was recorded — never a fee of zero, which would read on
   *  a report as a transfer that cost the company nothing.
   *
   *  Two fields rather than one, because a commission is charged in the sending
   *  bank's own money as often as in the transfer's, and the server books it at
   *  its own rate. `feeCurrency` is seeded from the payment's currency and stays
   *  editable; it is only ever SENT when `feeAmount` is filled in. */
  feeAmount: string
  feeCurrency: string
  receivedOn: string
}

/** The party's served codes worded for this form (S-22d2), under the party
 *  field; every other code reads as `fieldErrorKey` words it. */
const PAYMENT_FIELD_MESSAGES: Record<string, Record<string, ParseKeys>> = {
  counterpartyId: {
    required: 'finance.payments.party.pickOut',
    invalid: 'common.form.counterpartyUnknown',
    direction: 'common.form.partyDirection',
  },
  /* The client is not the case's — the case's client changed under an open
     sheet, or a stale tab. */
  clientId: { invalid: 'finance.payments.party.clientChanged' },
}

/** Records a NEW payment — the Finance page's «Внести платёж».
 *
 *  It has no edit mode: a pending payment is corrected by `PaymentAmendSheet`,
 *  and a confirmed one by a correcting entry. (The `record` mode this sheet
 *  once had was mounted by nothing but its own test, and was deleted rather
 *  than taught the S-22d2 party.) */
export function PaymentFormSheet({
  open,
  onClose,
  defaults,
  onSubmit,
  pending,
  failure,
}: {
  open: boolean
  onClose: () => void
  /** Facts the caller already knows about a payment that does not exist yet.
   *
   *  The values are SEEDED and then editable, never applied on submit. A form
   *  whose visible case field is overwritten behind the submit is worse than one
   *  that asks: what the reader can see and correct is what gets written. Which
   *  is also why a caller who cannot honestly answer a field omits it and leaves
   *  the box empty rather than seeding a blank that looks answered. */
  defaults?: Partial<PaymentDraft>
  onSubmit: (values: PaymentDraft) => void
  pending?: boolean
  /** What the SERVER refused, if it did, as the raw refusal: the banner words
   *  it, and a party code lands under the party field. Shown over the local
   *  hint: a rejected save is the more urgent news, and the fields it names may
   *  all look valid. */
  failure?: unknown
}) {
  const { t } = useTranslation()
  /* Both lists are read here rather than passed in, the way the quote sheet
     reads its clients: the server decides which cases and which entities this
     caller may see, so a parent passing them down would be a second, narrower
     answer to a question the endpoint already answers. */
  const { data: cases } = useCases()
  const { data: entities } = useIssuingEntityOptions()
  /* An outbound payment names a directory record, and the server refuses it
     (403 `held_by: counterparty.read`) to a reader who cannot read the
     directory — so such a reader is offered inbound only (S-22d2). An
     affordance; the server re-checks. */
  const { data: session } = useSession()
  const readsCounterparties = counterpartyRights(session).read
  const directions = readsCounterparties ? DIRECTIONS : INBOUND_ONLY

  const clientOfCase = (caseId: string) =>
    caseClientOf((cases ?? []).find((record) => record.id === caseId))

  const [draft, setDraft, touched, markTouched] = useFormDraft<PaymentDraft>(
    open ? 'new' : null,
    () => {
      // A new payment defaults to today: it is nearly always being entered
      // from a statement line that landed the same day. The caller's prefill
      // lands LAST, so a surface that knows the case, the direction of travel
      // or the party overrides the blank rather than the date.
      const seeded = { ...emptyPayment, receivedOn: today(), ...defaults }
      const direction = readsCounterparties ? seeded.direction : 'in'
      return {
        ...seeded,
        direction,
        party: defaults?.party ?? seedParty(direction, clientOfCase(seeded.caseId)),
      }
    },
  )

  const caseClient = clientOfCase(draft.caseId)

  /* OUR entity, defaulted from the case (N-24): a case is opened under one of
     the company's issuing entities, and money received against it nearly always
     reached that one. DERIVED at render rather than written into the draft:
     `issuingEntityId` stays '' until the reader picks, so the default follows
     the case when they change it, and a choice they made is never overwritten
     by a later case change. What the Select shows is what is sent.

     Only an entity this caller may name is a default. `entityId` is optional on
     the summary, and the options are the server's answer to which entities this
     reader sees — a case whose entity is not among them leaves the box empty
     rather than seeding an id the create would refuse. */
  const caseEntityId = (cases ?? []).find((record) => record.id === draft.caseId)?.entityId
  const defaultEntityId =
    caseEntityId !== undefined && (entities ?? []).some((entity) => entity.id === caseEntityId)
      ? caseEntityId
      : ''
  const issuingEntityId = draft.issuingEntityId || defaultEntityId

  /* The served refusal of the last save, worded under its field. */
  const served = fieldErrors(failure)
  const servedError = (field: string): string | undefined => {
    const code = served[field]?.[0]
    if (!code) return undefined
    return t(PAYMENT_FIELD_MESSAGES[field]?.[code] ?? fieldErrorKey(code))
  }

  /* Outbound with nobody picked is refused with the reason (S-22d's shape),
     which is also the field's hint; an inbound typed payer left blank is an
     ordinary required field. */
  const partyReady = partyComplete(draft.direction, draft.party)
  const pickRequired = draft.direction === 'out' && !partyReady

  const caseError = touched && !draft.caseId ? t('common.form.required') : undefined
  const referenceError =
    touched && !draft.reference.trim() ? t('common.form.required') : undefined
  const partyError =
    (touched && draft.direction === 'in' && !partyReady ? t('common.form.required') : undefined) ??
    servedError('counterpartyId') ??
    servedError('clientId') ??
    servedError('counterpartyName')
  const entityError =
    touched && !issuingEntityId ? t('common.form.required') : undefined
  const amountError =
    touched && !isValidAmount(draft.amount) ? t('common.form.invalidNumber') : undefined
  /* The fee is OPTIONAL, so a BLANK box is correct and only a filled-in one is
     checked. Absence is the ordinary answer — most transfers are recorded
     without anybody knowing what the bank took — and the server agrees: it
     refuses only a half-answered pair, per field. */
  const feeTyped = draft.feeAmount.trim() !== ''
  const feeError =
    touched && feeTyped && !isValidAmount(draft.feeAmount)
      ? t('common.form.invalidNumber')
      : undefined
  /* The case and the entity are required HERE as well as server-side, where
     they come back as 422s on `caseId` and `issuingEntityId`. Neither has a
     defensible default: guessing the case would book money against the wrong
     deal, and guessing which of our entities received it would put the sum in
     the wrong bank account's netting (FR-35). */
  const invalid =
    !draft.caseId ||
    !draft.reference.trim() ||
    !partyReady ||
    !issuingEntityId ||
    !isValidAmount(draft.amount) ||
    (feeTyped && !isValidAmount(draft.feeAmount))

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('finance.payments.record')}
      submitLabel={t('finance.payments.record')}
      pending={pending}
      blockedReason={pickRequired ? t('finance.payments.party.pickOut') : undefined}
      /* Our sentence, never `error.message`: that is the problem's `detail`,
         English written for a log (AD-27). */
      error={
        failure
          ? refusalMessage(failure, t)
          : touched && invalid
            ? t('common.form.fixFields')
            : undefined
      }
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit({ ...draft, issuingEntityId })
      }}
    >
      {/* The case first, because it decides what everything under it means: the
          rate basis the server books at, whose receivable moves, which
          documents this money could ever be allocated against — and, since
          S-22d2, whose client the party list offers. */}
      <Field
        label={t('finance.payments.case')}
        required
        error={caseError}
        hint={t('finance.payments.caseHint')}
      >
        {(props) => (
          <Select
            value={draft.caseId}
            onChange={(caseId) =>
              setDraft((current) => ({
                ...current,
                caseId,
                party: seedParty(current.direction, clientOfCase(caseId)),
              }))
            }
            options={(cases ?? []).map((record) => ({
              value: record.id,
              label: record.number,
              hint: record.clientName,
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            searchable
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('finance.payments.reference')}
        required
        error={referenceError}
        hint={t('finance.payments.referenceHint')}
      >
        {(props) => (
          <Input
            value={draft.reference}
            onChange={(e) => setDraft({ ...draft, reference: e.target.value })}
            className="font-mono"
            {...props}
          />
        )}
      </Field>

      <div className="flex gap-2.5">
        {/* Before the party, because it decides which parties exist: the
            direction's rule is the server's (S-22d2). A switch re-seeds the
            party — a payee picked for money out is not a payer of money in. */}
        <Field label={t('finance.payments.direction')} required className="flex-1">
          {(props) => (
            <Select
              value={draft.direction}
              onChange={(direction) =>
                setDraft((current) => ({
                  ...current,
                  direction,
                  party: seedParty(direction, clientOfCase(current.caseId)),
                }))
              }
              options={toOptions(directions, (direction) =>
                t(`finance.payments.${direction}`),
              )}
              {...props}
            />
          )}
        </Field>

        {/* Cash or bank. It is a fact about the payment, not a preference: a
            cash receipt at a border post and a SWIFT credit reconcile against
            different evidence, and the row has to say which it was. */}
        <Field label={t('finance.payments.method')} required className="flex-1">
          {(props) => (
            <Select
              value={draft.method}
              onChange={(method) => setDraft({ ...draft, method })}
              options={toOptions(METHODS, (method) =>
                t(`finance.payments.methods.${method}`),
              )}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* PICKED by record (S-22d2): outbound a directory counterparty or the
          case's client, inbound the case's client or a payer typed as the bank
          printed it. See PaymentPartyPicker. */}
      <Field
        label={t('finance.balances.counterparty')}
        required
        error={partyError}
        hint={
          pickRequired
            ? t('finance.payments.party.pickOut')
            : draft.direction === 'out'
              ? t('finance.payments.party.hintOut')
              : t('finance.payments.party.hintIn')
        }
      >
        {(props) => (
          <PaymentPartyPicker
            direction={draft.direction}
            value={draft.party}
            caseClient={caseClient}
            /* An updater: the picker's create sheet selects the new
               counterparty on a later render than the one that opened it. */
            onChange={(party) => setDraft((current) => ({ ...current, party }))}
            {...props}
          />
        )}
      </Field>

      {/* OURS, not theirs. "Issuing entity" reads both ways and the two produce
          different books: this is the company entity whose account the money
          reached or left, which is what netting is computed per (FR-35). The
          counterparty is the party above. */}
      <Field
        label={t('finance.payments.entity')}
        required
        error={entityError}
        hint={t('finance.payments.entityHint')}
      >
        {(props) => (
          <Select
            value={issuingEntityId}
            onChange={(picked) => setDraft({ ...draft, issuingEntityId: picked })}
            options={(entities ?? []).map((entity) => ({
              value: entity.id,
              label: entity.name,
            }))}
            placeholder={t('common.form.selectPlaceholder')}
            {...props}
          />
        )}
      </Field>

      <div className="flex gap-2.5">
        <Field
          label={t('common.fields.amount')}
          required
          error={amountError}
          className="flex-1"
        >
          {(props) => (
            <Input
              inputMode="decimal"
              value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
              className="asa-num"
              {...props}
            />
          )}
        </Field>

        <Field label={t('finance.payments.currency')} className="w-28 shrink-0">
          {(props) => (
            <Select
              value={draft.currency}
              onChange={(currency) => setDraft({ ...draft, currency })}
              // A currency code is already its own label.
              options={toOptions(CURRENCIES, (currency) => currency)}
              {...props}
            />
          )}
        </Field>
      </div>

      {/* The bank's charge for moving the money — its OWN labelled area, not a
          third box crammed beside the amount and its currency.

          The separation is the point rather than the layout: the two figures
          mean different things and one of them is dangerous to confuse. What
          settles a receivable is the AMOUNT above; the fee is what the transfer
          COST the company and settles nothing (AD-11) — a 5 000 transfer with a
          25 commission settles 5 000 of debt and cost 5 025. Three amount boxes
          in one row invite exactly that addition. */}
      <div className="border-line-hair flex flex-col gap-2 border-t pt-3">
        <span className="asa-eyebrow">{t('finance.payments.feeHeading')}</span>

        <div className="flex gap-2.5">
          <Field
            label={t('finance.payments.fee')}
            error={feeError}
            hint={t('finance.payments.feeHint')}
            className="flex-1"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.feeAmount}
                onChange={(e) => setDraft({ ...draft, feeAmount: e.target.value })}
                className="asa-num"
                {...props}
              />
            )}
          </Field>

          {/* Defaults to the payment's own currency and stays editable: a
              commission is charged in the sending bank's money as often as in
              the transfer's, and the server books it at its own rate for the
              same day. An empty draft value MEANS "the payment's", so changing
              the payment's currency carries the fee along until somebody pins
              it deliberately. */}
          <Field label={t('finance.payments.feeCurrency')} className="w-28 shrink-0">
            {(props) => (
              <Select
                value={draft.feeCurrency === '' ? draft.currency : draft.feeCurrency}
                onChange={(feeCurrency) => setDraft({ ...draft, feeCurrency })}
                options={toOptions(CURRENCIES, (currency) => currency)}
                {...props}
              />
            )}
          </Field>
        </div>
      </div>

      {/* Cash cannot land in the future: a received date past today is always a
          typo, and the picker refuses it rather than booking it. */}
      <Field label={t('finance.payments.received')} required>
        {(props) => (
          <DatePicker
            value={draft.receivedOn}
            max={today()}
            onChange={(receivedOn) => setDraft({ ...draft, receivedOn })}
            {...props}
          />
        )}
      </Field>

      {/* Allocation is a separate act. Cash lands unallocated and stays visible
          as such until someone applies it — it is never auto-applied (AD-11),
          and this sheet does not offer to. */}
      <p className="text-text-muted text-[12px] leading-snug font-light">
        {t('finance.payments.allocationNote')}
      </p>
    </FormSheet>
  )
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

const emptyPayment: PaymentDraft = {
  caseId: '',
  reference: '',
  // Re-seeded from the case on open and on every case or direction change.
  party: NO_PARTY,
  direction: 'in',
  // Bank transfer rather than cash: it is the overwhelmingly common case, and
  // unlike the case and the entity a wrong default here is visible on the row
  // and costs a correction rather than a misposting.
  method: 'bank_transfer',
  issuingEntityId: '',
  amount: '',
  currency: 'USD',
  // Both blank. No fee is the ordinary answer — most transfers are recorded
  // without anybody knowing what the bank took — and a blank currency means
  // "the payment's own", which is what the control renders and the payload
  // resolves. Seeding a code here would pin the fee to USD the moment the
  // payment currency was changed to something else.
  feeAmount: '',
  feeCurrency: '',
  receivedOn: '',
}

/* ── The debit note ───────────────────────────────────────────────────────────
 *
 * ONE raise form, both screens (00-decisions §4). The case's Invoices section
 * and the Money page differ in what they PREFILL and what they FILTER, never in
 * what they ask. A second sheet would drift in wording before it drifted in
 * behaviour, and the two surfaces would then disagree about a document in front
 * of the person holding it.
 *
 * THE INVOICE BRANCH IS GONE, and it is worth a paragraph so nobody restores
 * it. This sheet used to raise either kind behind a "kind" select, and the
 * invoice half asked four things: a currency, two dates and the client's own
 * reference. Every one of them has a defensible answer without a form — the
 * currency is the rate's, the dates are today, the reference is a field on the
 * invoice — and none of them is the question somebody raising an invoice
 * actually has, which is "how much of the agreed rate is left to bill". That
 * question needs the charges, the AD-10 ceiling and the AD-9 booking in view
 * together, and a 440px column holds one of the three. So the invoice is raised
 * from `NewDocumentMenu` with no form at all and opens on its own PAGE
 * (`routes/CaseInvoicePage.tsx`); what is left here is the document that really
 * does need a body before it can exist.
 *
 * POST /cases/:id/debit-notes takes the AD-26 counterparty pair and the CHARGES
 * together, in one request. The pair is always the case's own client (S-22a):
 * the client is the only outside party this system bills, so the sheet names
 * the payer and offers no choice — one possible payer is not a question. A debit note with no charges is a demand for money
 * with no stated reason, and creating it in one request is what stops that
 * moment existing at all — so the lines editor is on this form, and a note
 * without one complete line cannot be submitted. `DebitNotePayload` is the
 * deliberate opposite of the invoice's create, which takes a header and grows
 * its charges one guarded round trip at a time.
 *
 * Nothing about the recipient's printed name or address is collected. Both are
 * RESOLVED from the party the counterparty pair names (AD-26), and a body that
 * could state them could address a demand to a company nobody picked. The
 * free-text counterparty and entity boxes this sheet used to carry went with the
 * `internal/showcase` fixture that accepted them, and the payer picker over the
 * case's client and participants went with the case's participants (S-22a).
 */

/** Who a NEW debit note bills: the case's own client (S-22a), as the CALLER
 *  holds it.
 *
 *  Passed in rather than fetched here, because it is a fact of the record the
 *  caller already has open — and the id must be the CASE's client: the create
 *  refuses any other with `counterparty: client-only`. Absent while the record
 *  is loading, and the sheet then refuses to submit rather than guess. */
export interface DebitNotePayer {
  id: string
  name: string
}

/** The sheet's own form state — NOT a payload.
 *
 *  Shared with the header amend below, which is why it is one flat shape rather
 *  than the payload itself: the raise sends the charges and the amend does not,
 *  and one draft type that both seed keeps the two forms from growing different
 *  fields for one document. Money and every figure stay decimal STRINGS
 *  throughout (AD-9); nothing here is ever widened to a number. */
export interface DocumentDraft {
  /** The AD-9 booking currency, frozen at creation. */
  currency: InvoiceCurrency

  /** What the note says about ITSELF — its own date. */
  documentDate: string
  /** THEIR reference for the job — a purchase order, a booking number. Printed
   *  on the paper we send them, so it is typed here rather than looked up. */
  reference: string

  /** The charges. */
  lines: LineDraft[]
}

/** Raising a NEW debit note against one case.
 *
 *  `onSubmit` hands out exactly the `RaiseDocumentPayload` that
 *  `useRaiseDocument` takes — this sheet builds the wire shape rather than a
 *  caller mapping a draft onto it. A `toDocumentPayload` living beside the
 *  mutation is what the fixture had, and it is exactly where "invoice with
 *  lines" became expressible. */
export function DebitNoteFormSheet({
  open,
  onClose,
  caseId,
  payer,
  defaults,
  onSubmit,
  pending,
  /** What the SERVER refused, if it did — shown over the local hint, as on the
   *  payment sheet. Our sentence and never the server's `detail`, which is
   *  English written for a log (AD-22, AD-27); callers pass `refusalMessage`. */
  submitError,
  failure,
}: {
  open: boolean
  onClose: () => void
  /** The case this paper is raised against. The create is nested under it, and
   *  it is also the id the row scope is checked against. */
  caseId: string
  /** The case's client — the only party a new debit note may bill (S-22a). */
  payer: DebitNotePayer | undefined
  /** Facts the caller already knows about a document that does not exist yet —
   *  the case section seeds the charges it picked off the case's costs.
   *
   *  Deliberately separate from an existing record: a partial record would seed
   *  the same fields but would also title the sheet as an edit and make it claim
   *  to be changing a row that was never raised. This is a NEW document that
   *  starts further along, which is a different thing. */
  defaults?: Partial<DocumentDraft>
  onSubmit: (payload: RaiseDocumentPayload) => void
  pending?: boolean
  submitError?: string
  /** The raw refusal behind `submitError`, read for the payer's own code: a
   *  `counterparty` field error (`client-only` — the case's client changed
   *  under an open sheet, or a stale tab) is worded by its code, because the
   *  generic «highlighted fields» sentence would point at a field nobody can
   *  edit here. */
  failure?: unknown
}) {
  const { t } = useTranslation()

  const [draft, setDraft, touched, markTouched] = useFormDraft<DocumentDraft>(
    open ? 'new' : null,
    () => ({
      ...emptyDocument,
      documentDate: bookingToday(),
      ...defaults,
    }),
  )

  const payerError =
    touched && payer === undefined ? t('common.form.required') : undefined
  const payerCode = fieldErrors(failure)['counterparty']?.[0]
  const refusal = payerCode === undefined ? submitError : t(fieldErrorKey(payerCode))

  /* The note date is NOT required: its create route defaults it, so an empty
     box is a legal request rather than a refusal waiting to happen.

     Every line must be complete, AND there must be one. A half-typed row would
     otherwise be dropped silently on submit and the charge it was going to carry
     would simply not be billed — a failure nobody notices until the recipient
     underpays; an empty editor would post a demand with no stated reason, which
     the create route refuses outright. Both are said on screen by the editor
     itself rather than only by the sheet's error line. */
  const invalid =
    payer === undefined ||
    draft.lines.length === 0 ||
    !draft.lines.every(isLineComplete)

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('finance.documents.new.debitNote')}
      submitLabel={t('finance.documents.raise.submit')}
      pending={pending}
      error={refusal ?? (touched && invalid ? t('common.form.fixFields') : undefined)}
      onSubmit={() => {
        markTouched()
        if (invalid || payer === undefined) return
        onSubmit(toRaisePayload(draft, caseId, payer))
      }}
    >
      <PayerField
        name={payer?.name ?? ''}
        hint={t('finance.documents.raise.counterpartyHint')}
        error={payerError}
      />

      <CurrencyField
        value={draft.currency}
        onChange={(currency) => setDraft({ ...draft, currency })}
      />

      <DebitNoteHeaderFields
        documentDate={draft.documentDate}
        reference={draft.reference}
        onChange={(change) => setDraft({ ...draft, ...change })}
      />

      {/* The charges. Last in the sheet because it is the tallest block and the
          one that grows — the header facts stay where they were put while rows
          are being added below them. */}
      <DocumentLines
        lines={draft.lines}
        currency={draft.currency}
        showErrors={touched}
        onChange={(lines) => setDraft({ ...draft, lines })}
      />
    </FormSheet>
  )
}

/** Amending a DRAFT debit note's header — `PUT /cases/:id/debit-notes/:noteId`.
 *
 *  A second entry point over the same fields rather than a `record` prop on the
 *  sheet above, because the two acts ask different questions. The raise decides
 *  the AD-9 booking — the currency, and with it the rate and the USD equivalent —
 *  and the edit route cannot move any of it, so the currency control is ABSENT
 *  here and its absence IS the freeze. The charges are absent for the same kind
 *  of reason: `lines` are read and discarded by that route, and the three line
 *  endpoints amend a note that always has at least one. A sheet that showed
 *  either control greyed out would be describing a rule as a UI state.
 *
 *  WHO OWES IT is absent from the write for the same kind of reason (S-22a): the
 *  route keeps the stored payer and printed recipient whatever a body says, so
 *  the payer is shown read-only and the payload carries no counterparty pair. An
 *  old note billed to a participant keeps that party, and only its date and
 *  reference move.
 *
 *  Only a DRAFT reaches this. An issued document is not editable and the
 *  affordance is simply absent — the caller does not offer it (AC 9) — and the
 *  server refuses the write regardless, which is the guard; this is not it. */
export function DebitNoteHeaderSheet({
  open,
  onClose,
  record,
  onSubmit,
  pending,
  submitError,
}: {
  open: boolean
  onClose: () => void
  record: DebitNote
  onSubmit: (payload: DebitNoteHeaderPayload) => void
  pending?: boolean
  submitError?: string
}) {
  const { t } = useTranslation()

  /* The AD-6 group travels together: a caller without agent-identity visibility
     receives the kind, the id, the name and the address as hidden markers. The
     field then says so — never a marker and never a blank, which would read as
     a note billed to nobody — and the save stays open: the date and the
     reference name nobody, and the PUT re-states no payer. */
  const payerName = isSuppressed(record.recipientName)
    ? t('common.suppressed.reason')
    : (record.recipientName ?? '')

  const [draft, setDraft] = useFormDraft<DocumentDraft>(open ? record.id : null, () => ({
    ...emptyDocument,
    documentDate: record.noteDate,
    reference: record.recipientReference ?? '',
  }))

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('finance.documents.edit')}
      pending={pending}
      error={submitError}
      onSubmit={() => onSubmit(toAmendPayload(draft, record))}
    >
      <PayerField name={payerName} hint={t('finance.documents.raise.counterpartyFixed')} />

      <DebitNoteHeaderFields
        documentDate={draft.documentDate}
        reference={draft.reference}
        onChange={(change) => setDraft({ ...draft, ...change })}
      />
    </FormSheet>
  )
}

/** Who owes the note, READ-ONLY on both sheets (S-22a).
 *
 *  A read-only input rather than a line of text, so the payer sits where the
 *  picker used to and reads as a field of the document — with its label, under
 *  the same rule as every other field — rather than as a caption. Nothing here
 *  is ever sent: the raise sends the case's client id and the amend sends no
 *  pair at all. */
function PayerField({
  name,
  hint,
  error,
}: {
  name: string
  hint: string
  error?: string
}) {
  const { t } = useTranslation()

  return (
    <Field label={t('finance.documents.raise.counterparty')} error={error} hint={hint}>
      {(props) => <Input value={name === '' ? '—' : name} readOnly {...props} />}
    </Field>
  )
}

/** The document currency (AC-6, FR-61) — three codes.
 *
 *  The same list and the same labels the invoice side prints in, read from
 *  `invoice.ts` rather than restated: the server checks BOTH documents against
 *  `finance.PrintsIn`, so a second list here would be a second answer that only
 *  fails at the round trip. Choosing it books the rate and its date, and nothing
 *  later can move either — which is why the hint says so before the choice
 *  rather than after it. */
function CurrencyField({
  value,
  onChange,
}: {
  value: InvoiceCurrency
  onChange: (currency: InvoiceCurrency) => void
}) {
  const { t } = useTranslation()

  return (
    <Field
      label={t('finance.documents.raise.currency')}
      required
      hint={t('finance.invoices.currencyHint')}
    >
      {(props) => (
        <Select
          value={value}
          onChange={onChange}
          // The code IS the label for two of the three. The third reads RMB on
          // screen and travels as CNY, which is the one place those two
          // spellings of one currency are allowed to differ.
          options={toOptions(INVOICE_CURRENCIES, currencyLabel)}
          {...props}
        />
      )}
    </Field>
  )
}

/** The two header facts a debit note states about itself, shared by the raise
 *  and the amend so the two cannot grow different labels for one field. */
function DebitNoteHeaderFields({
  documentDate,
  reference,
  onChange,
}: {
  documentDate: string
  reference: string
  onChange: (change: Partial<DocumentDraft>) => void
}) {
  const { t } = useTranslation()

  return (
    <div className="flex gap-2.5">
      <Field label={t('finance.documents.raise.noteDate')} className="flex-1">
        {(props) => (
          <DatePicker
            value={documentDate}
            onChange={(next) => onChange({ documentDate: next })}
            {...props}
          />
        )}
      </Field>

      {/* THEIR reference for the job — a purchase order, a booking number. It
          identifies a shipment rather than a company, which is why it is typed
          here and is not part of the suppressed group above. */}
      <Field label={t('finance.documents.raise.reference')} className="flex-1">
        {(props) => (
          <Input
            value={reference}
            onChange={(e) => onChange({ reference: e.target.value })}
            {...props}
          />
        )}
      </Field>
    </div>
  )
}

/** The draft as the create route takes it.
 *
 *  Built HERE rather than in a mapper beside the mutation, because the mutation
 *  takes a union and this is the only place that knows which arm was filled in.
 *  `RaiseDocumentPayload` still has an invoice arm — `useRaiseDocument` raises
 *  both kinds and the Money page's row actions read both — but no form produces
 *  one any more: a client invoice is opened by `useRaiseInvoiceDraft` with a
 *  header nobody typed.
 *
 *  Both optional dates are OMITTED when blank rather than sent as '': the server
 *  defaults the note date to today and the economic date to the note date, and
 *  an empty string on the wire is a parse failure rather than a default. */
function toRaisePayload(
  draft: DocumentDraft,
  caseId: string,
  payer: DebitNotePayer,
): RaiseDocumentPayload {
  const reference = draft.reference.trim()
  return {
    kind: 'debit-note',
    caseId,
    note: {
      counterpartyKind: 'client',
      counterpartyId: payer.id,
      currency: draft.currency,
      ...(draft.documentDate.trim() === '' ? {} : { noteDate: draft.documentDate }),
      ...(reference === '' ? {} : { recipientReference: reference }),
      lines: draft.lines.map(toDebitNoteLine),
    },
  }
}

/** The draft as the header edit takes it.
 *
 *  No counterparty pair (S-22a): the route keeps the stored payer, so a pair
 *  here would be a change this client believes it made and the server silently
 *  did not. The currency is echoed from the RECORD rather than from the draft,
 *  and the lines are not sent at all. That route reads both and discards them — an edit
 *  form that round-trips the whole object is the ordinary client — but echoing
 *  the note's own frozen currency is the honest version of a field nothing on
 *  this form can move, where a value typed somewhere else would be a change this
 *  client believes it made and the server silently did not. */
function toAmendPayload(draft: DocumentDraft, record: DebitNote): DebitNoteHeaderPayload {
  const reference = draft.reference.trim()
  return {
    currency: record.currency,
    ...(draft.documentDate.trim() === '' ? {} : { noteDate: draft.documentDate }),
    ...(reference === '' ? {} : { recipientReference: reference }),
  }
}

/** One charge as the create route takes it.
 *
 *  NO AMOUNT: it is `unitPrice × quantity`, derived once on the server. The
 *  running figure beside each row in the editor is a preview of that arithmetic
 *  and never the payload — a typed amount would be a fourth number able to
 *  disagree with the other three on the face of the document.
 *
 *  Normalised and trimmed exactly as `useWriteDebitNoteLine` does it, because
 *  these rows and the ones added later on the line routes print in one table and
 *  must arrive spelled the same way. The qualifying note is sent as '' rather
 *  than omitted: the printed row always has that line. */
function toDebitNoteLine(line: LineDraft): DebitNoteLinePayload {
  return {
    description: line.description.trim(),
    note: line.note.trim(),
    unitPrice: normaliseDecimal(line.unitPrice),
    quantity: normaliseDecimal(line.quantity),
  }
}

/** A blank note. The date is filled in by the seeding caller, which is where
 *  "today" is decided — a constant evaluated at module load would answer with
 *  the day the tab was opened. */
const emptyDocument: DocumentDraft = {
  currency: 'USD',
  documentDate: '',
  reference: '',
  lines: [],
}
